import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { teamService } from '@/api/teams'

export const useTeamsStore = defineStore('teams', () => {

  const teams = ref([])
  const selectedTeam = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const searchQuery = ref('')
  const pagination = ref({
    total: 0,
    page: 1,
    limit: 8,
    totalPages: 0,
  })

  let searchTimeout = null

  const hasTeams = computed(() => teams.value.length > 0)
  const selectedTeamId = computed(() => selectedTeam.value?.id)


  const fetchTeams = async (page = null, limit = null, search = null) => {
    loading.value = true
    error.value = null
    try {
      
      const pageToUse = page !== null ? page : pagination.value.page
      const limitToUse = limit !== null ? limit : pagination.value.limit
      const searchToUse = search !== null ? search : searchQuery.value
      
      const result = await teamService.getTeams(
        pageToUse,
        limitToUse,
        searchToUse,
      )
      teams.value = result.teams
      pagination.value = result.pagination

      if (selectedTeam.value) {
        selectedTeam.value = result.teams.find((t) => t.id === selectedTeam.value.id)
      }
    } catch (err) {
      console.error('Fehler beim Laden der Teams:', err)
      error.value = 'Die Teams konnten nicht geladen werden. Bitte versuch es später erneut.'
    } finally {
      loading.value = false
    }
  }

  const loadTeamById = async (teamId) => {
    try {
      loading.value = true
      const team = await teamService.getTeam(teamId)
      selectedTeam.value = team
      return team
    } catch (err) {
      console.error('Fehler beim Laden des Teams:', err)
      error.value = 'Das Team konnte nicht geladen werden.'
      return null
    } finally {
      loading.value = false
    }
  }

  const handleSearch = () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      pagination.value.page = 1
      fetchTeams(1, pagination.value.limit, searchQuery.value)
    }, 300)
  }

  const handlePageChange = (newPage) => {
    pagination.value.page = newPage
    fetchTeams(newPage, pagination.value.limit, searchQuery.value)
  }

  const setSearchQuery = (query) => {
    searchQuery.value = query
    handleSearch()
  }

  const selectTeam = (team) => {
    selectedTeam.value = team
  }

  const clearSelectedTeam = () => {
    selectedTeam.value = null
  }


  const viewTeam = (team) => {
    selectTeam(team)

  }

  const editTeam = (team) => {
    selectTeam(team)

  }

  const inviteMembers = (team) => {
    selectTeam(team)

  }

  const manageSchema = (team) => {
    selectTeam(team)

  }

  const createProject = (team) => {
    selectTeam(team)
  }

  const teamCreated = async (team) => {
    selectTeam(team)
    await fetchTeams(pagination.value.page, pagination.value.limit, searchQuery.value)
  }

  const teamUpdated = async (team) => {
    selectTeam(team)
    await fetchTeams(pagination.value.page, pagination.value.limit, searchQuery.value)
  }

  const teamDeleted = async () => {
    clearSelectedTeam()
    await fetchTeams(pagination.value.page, pagination.value.limit, searchQuery.value)
  }

  const schemaUpdated = () => {
    
  }

  const refreshTeams = async () => {
    await fetchTeams(pagination.value.page, pagination.value.limit, searchQuery.value)
  }

  // Reset teams to default pagination when switching contexts
  const resetToDefaultPagination = async () => {
    pagination.value = {
      total: 0,
      page: 1,
      limit: 8, // Default limit for teams list
      totalPages: 0,
    }
    await fetchTeams(1, 8, searchQuery.value)
  }

  const updateTeamReceiptSettings = async (teamId, settings) => {
    try {
      const updatedSettings = await teamService.updateTeamReceiptSettings(teamId, settings)
      
      
      if (selectedTeam.value && selectedTeam.value.id === teamId) {
        selectedTeam.value = {
          ...selectedTeam.value,
          ...updatedSettings
        }
      }
      
      
      const teamIndex = teams.value.findIndex(team => team.id === teamId)
      if (teamIndex !== -1) {
        teams.value[teamIndex] = {
          ...teams.value[teamIndex],
          ...updatedSettings
        }
      }
      
      return updatedSettings
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Quittungseinstellungen:', err)
      throw err
    }
  }

  const resetStore = () => {
    teams.value = []
    selectedTeam.value = null
    loading.value = false
    error.value = null
    searchQuery.value = ''
    pagination.value = {
      total: 0,
      page: 1,
      limit: 8,
      totalPages: 0,
    }
  }

  return {
    teams,
    selectedTeam,
    loading,
    error,
    searchQuery,
    pagination,

    hasTeams,
    selectedTeamId,

    fetchTeams,
    loadTeamById,
    handleSearch,
    handlePageChange,
    setSearchQuery,
    selectTeam,
    clearSelectedTeam,


    viewTeam,
    editTeam,
    inviteMembers,
    manageSchema,
    createProject,
    teamCreated,
    teamUpdated,
    teamDeleted,
    schemaUpdated,
    refreshTeams,
    resetToDefaultPagination,
    updateTeamReceiptSettings,
    resetStore,
  }
})
